import {
  afterAnimation,
  MASK_POSITION,
  type MaskLayers,
  penCurve,
  readsRtl,
  setMask,
  sigAnimate,
  sigMs,
  sigNumber,
} from './motion'
import { writeRule } from './rules'

/**
 * The pen (DESIGN.md "The signature layer"): the screen's title writes itself
 * and the rules on screen are written after it (`rules.ts`).
 *
 * The title stays ONE text run (The Unbroken Arabic Rule): masks reveal the
 * whole run from the reading start, one mask layer per rendered line, so the
 * browser keeps shaping the Arabic. The real title is in the DOM throughout,
 * and every cover, copy and mask exists only while it moves.
 */

interface Line {
  left: number
  right: number
  top: number
  bottom: number
}

/** The rendered lines of a text run: its client rects grouped by line (bidi fragments merge). */
function textLines(el: HTMLElement): Line[] {
  const range = document.createRange()
  range.selectNodeContents(el)
  const lines: Line[] = []
  for (const rect of range.getClientRects()) {
    if (!rect.width || !rect.height) continue
    const mid = (rect.top + rect.bottom) / 2
    const line = lines.find((l) => mid > l.top && mid < l.bottom)
    if (line) {
      line.left = Math.min(line.left, rect.left)
      line.right = Math.max(line.right, rect.right)
      line.top = Math.min(line.top, rect.top)
      line.bottom = Math.max(line.bottom, rect.bottom)
    } else {
      lines.push({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom })
    }
  }
  return lines.sort((a, b) => a.top - b.top)
}

/**
 * One mask layer per line: opaque behind the pen, a soft nib, clear ahead.
 * The pen travels the lines in reading order and a finished line stays open.
 */
function penPlan(el: HTMLElement) {
  const box = el.getBoundingClientRect()
  const lines = textLines(el)
  if (!box.width || lines.length === 0) return null
  const rtl = readsRtl(el)
  const fontSize = Number.parseFloat(getComputedStyle(el).fontSize) || 24
  const nib = Math.max(8, Math.round(fontSize * sigNumber('--sig-nib', 0.75)))
  const width = box.width
  const bands = lines.map((line, i, all) => ({
    start: rtl ? box.right - line.right : line.left - box.left,
    length: line.right - line.left,
    top: i ? ((all[i - 1] as Line).bottom + line.top) / 2 - box.top : 0,
    bottom:
      i < all.length - 1 ? (line.bottom + (all[i + 1] as Line).top) / 2 - box.top : box.height,
  }))
  // One layer: the opaque run, the nib, then the clear run.
  const opaque = width + nib
  const layer = opaque + nib + width + nib
  const total = bands.reduce((sum, band) => sum + band.length, 0)
  const x = (front: number) => (rtl ? width - front - layer + opaque : front - opaque)
  const at = (written: number) => {
    let before = 0
    return bands
      .map((band) => {
        const own = written - before
        before += band.length
        const front =
          own >= band.length
            ? width + nib
            : own <= 0
              ? band.start - nib
              : band.start - nib + (own / band.length) * (band.length + nib)
        return `${x(front)}px ${band.top}px`
      })
      .join(', ')
  }
  const image = `linear-gradient(to ${rtl ? 'left' : 'right'}, #000 ${opaque}px, transparent ${opaque + nib}px)`
  const rest: MaskLayers = {
    Image: bands.map(() => image).join(', '),
    Size: bands.map((band) => `${layer}px ${band.bottom - band.top}px`).join(', '),
    Repeat: 'no-repeat',
    Position: at(total),
  }
  return {
    box,
    oneLine: bands.length === 1,
    rest,
    frames: penCurve((p) => ({ [MASK_POSITION]: at(total * p) })) as Keyframe[],
  }
}

/** The face and the measure the outline copy must share with the title. */
const OUTLINE_FONT = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-stretch',
  'letter-spacing',
  'word-spacing',
  'line-height',
  'font-feature-settings',
  'font-variation-settings',
  'font-variant-numeric',
  'font-kerning',
  'text-transform',
  'text-align',
  'text-wrap',
  'direction',
]

const writing = new WeakMap<HTMLElement, () => void>()

/**
 * The pen writes the title: an `aria-hidden` outline copy of the whole run
 * is revealed by the pen (0-620ms), the real title's ink follows 150ms behind,
 * the outline lifts (620-780ms), and the title's satr is written on the same
 * curve with the kashida drawing on to the end of the line (520-880ms).
 */
export function writeTitle(title: HTMLElement) {
  writing.get(title)?.()
  const plan = penPlan(title)
  // The outline lies in the title's positioned container (the satr line).
  const holder =
    title.offsetParent instanceof HTMLElement ? title.offsetParent : title.parentElement
  if (!plan || !holder) return
  const holderBox = holder.getBoundingClientRect()
  const cs = getComputedStyle(title)

  const outline = document.createElement('span')
  outline.setAttribute('aria-hidden', 'true')
  outline.dataset.signature = 'outline'
  for (const node of title.childNodes) outline.appendChild(node.cloneNode(true))
  for (const withId of outline.querySelectorAll('[id]')) withId.removeAttribute('id')
  for (const property of OUTLINE_FONT) {
    outline.style.setProperty(property, cs.getPropertyValue(property))
  }
  Object.assign(outline.style, {
    position: 'absolute',
    margin: '0',
    padding: '0',
    pointerEvents: 'none',
    zIndex: '2',
    color: 'transparent',
    width: `${plan.box.width}px`,
    top: `${plan.box.top - holderBox.top - holder.clientTop}px`,
    left: `${plan.box.left - holderBox.left - holder.clientLeft}px`,
    whiteSpace: plan.oneLine ? 'nowrap' : '',
  })
  outline.style.setProperty(
    '-webkit-text-stroke',
    `var(--sig-pen-stroke, 1px) var(--sig-outline-ink, currentColor)`,
  )
  outline.style.setProperty('--sig-outline-ink', cs.color)
  holder.appendChild(outline)

  const pen = sigMs('--sig-dur-pen', 620)
  setMask(title, plan.rest)
  setMask(outline, plan.rest)
  const traced = sigAnimate(outline, plan.frames, { duration: pen, easing: 'linear', fill: 'both' })
  const ink = sigAnimate(title, plan.frames, {
    duration: pen,
    delay: sigMs('--sig-delay-ink', 150),
    easing: 'linear',
    fill: 'both',
  })
  const lift = sigAnimate(outline, [{ opacity: 1 }, { opacity: 0 }], {
    duration: sigMs('--sig-dur-outline-fade', 160),
    delay: pen,
    easing: 'linear',
    fill: 'both',
  })

  let done = false
  const finish = () => {
    if (done) return
    done = true
    traced.cancel()
    lift.cancel()
    ink.cancel()
    outline.remove()
    setMask(title, null)
    if (writing.get(title) === finish) writing.delete(title)
  }
  writing.set(title, finish)
  afterAnimation(lift, () => outline.remove())
  // However the ink ends (finished, or cancelled by anything), the title is left complete.
  afterAnimation(ink, finish)

  const satr = title.closest<HTMLElement>('[data-satr]')
  if (satr) writeRule(satr, 0, true)
}
